import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, Search, Download, ChevronLeft, Clock, CheckCircle, XCircle, Car, Calendar, RefreshCw, MapPin, Eye, Mail, History } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const ResidentPassesContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchPasses(); }, [page, statusFilter, selectedSite]);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/passes?pass_type=resident&page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&reg_number=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      
      const res = await axios.get(url);
      setPasses(res.data.passes || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Pass ID", "Name", "VRM", "Amount", "Status", "Valid From", "Valid Until", "Created"];
    const rows = passes.map(p => [p.pass_id, p.resident_name, p.reg_number, p.amount_display, p.status, p.valid_from, p.valid_until, new Date(p.created_at).toLocaleString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `resident-passes-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "pending": return <Clock className="w-4 h-4 text-yellow-500" />;
      case "expired": return <XCircle className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-resident-passes">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Resident Passes</h1><p className="text-sm text-gray-500">{total} passes</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchPasses()} placeholder="Search by VRM, name..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Resident</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VRM</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Valid Until</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={8} className="px-4 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>)
              : passes.length === 0 ? (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No passes found</td></tr>)
              : passes.map((pass) => (
                <tr key={pass.pass_id} onClick={() => navigate(`/admin/commercial/resident-passes/${pass.pass_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Users className="w-5 h-5 text-purple-600" /></div>
                      <div><p className="font-medium text-gray-900">{pass.resident_name}</p><p className="text-xs text-gray-500">{pass.pass_id}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-sm bg-yellow-100 px-2 py-1 rounded">{pass.reg_number}</span></td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">{pass.amount_display}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">{getStatusIcon(pass.status)}<span className={`px-2 py-1 text-xs font-medium rounded-full ${pass.status === "active" ? "bg-green-100 text-green-700" : pass.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>{pass.status}</span></div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{pass.valid_until ? new Date(pass.valid_until).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">{pass.site_id ? <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /></span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(pass.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/admin/commercial/resident-passes/${pass.pass_id}`)} className="p-2 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-gray-500" /></button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg" title="Email"><Mail className="w-4 h-4 text-blue-500" /></button>
                      <button onClick={() => navigate(`/admin/commercial/audit?entity_id=${pass.pass_id}`)} className="p-2 hover:bg-gray-100 rounded-lg" title="History"><History className="w-4 h-4 text-purple-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialResidentPasses() {
  return (
    <CommercialProvider>
      <ResidentPassesContent />
    </CommercialProvider>
  );
}
